import type { ComponentPropsWithRef } from 'react';

import { useTranslation } from 'react-i18next';

import type { CommunityProfile } from '~/api/community/types';

import { Avatar, Card } from '~/components';
import { CommunityProfileFooter, getCommunityProfileNames } from '~/features/community';
import { useIntl } from '~/hooks/useIntl';
import { useMediaQuery } from '~/hooks/useMediaQuery';
import { breakpoints } from '~/styles/media.css';

import {
  avatarStyle,
  contentStyle,
  footerStyle,
  fullNameStyle,
  headerStyle,
  openToWorkStyle,
  subtitleStyle,
  titleStyle,
  titleWrapperStyle,
  wrapperStyle,
} from './CommunityProfileCard.css';

type Props = ComponentPropsWithRef<'div'> & {
  profile: CommunityProfile;
};

export const CommunityProfileCard = ({ profile, ...delegated }: Props) => {
  const { t } = useTranslation(['common', 'community']);

  const { formatConjunctiveList } = useIntl();

  const isTablet = useMediaQuery(breakpoints.medium);

  const { fullName, name } = getCommunityProfileNames(profile);

  return (
    <Card.Group className={wrapperStyle} {...delegated}>
      <Card.Root className={headerStyle} variant="primary-light">
        <Avatar
          aria-label={t('community:avatar', { name })}
          className={avatarStyle}
          fallback={name.charAt(0)}
          size={isTablet ? 60 : 128}
          src={profile.profilePictureUrl ?? undefined}
        />

        <div className={contentStyle}>
          <div className={titleWrapperStyle}>
            <span className={titleStyle}>{name}</span>

            {fullName && <p className={fullNameStyle}>{fullName}</p>}

            {profile.regions.length > 0 && (
              <p className={subtitleStyle}>{formatConjunctiveList(profile.regions.map((region) => t(`community:regions.${region}`)))}</p>
            )}
          </div>
        </div>
      </Card.Root>

      <Card.Root className={footerStyle}>
        {profile.openToWork && <span className={openToWorkStyle}>{t('community:profileCard.openToWork')}</span>}

        <CommunityProfileFooter profile={profile} />
      </Card.Root>
    </Card.Group>
  );
};
